import * as slideDataWCS from '../constants/slidesScopeWithoutCommonSense';
import messages from '../lang/preferences';

const LANDING_MESSAGE_KEYS = [
  'LOGIN_TEXT',
  'LOGIN_TITLE',
  'LOGIN_EMAIL',
  'LOGIN_KEY',
  'LOGIN_BTN_CONNEXION',
  'INFO_FOOTER_TITLE',
  'INFO_FOOTER_TEXT'
];

const REPORT_EXACT_MESSAGE_KEYS = new Set([
  'REPORT_TITLE',
  'CANDIDATE',
  'TEST_DATE',
  'TEST_TIME_TAKEN',
  'PAGE',
  'DEFINITIONS',
  'LOWERTHIRD',
  'UPPERTHIRD'
]);

const normalizeLangForFile = (lang) => {
  const raw = String(lang || '').trim();
  if (!raw) return 'en';
  const lowered = raw.toLowerCase();

  if (lowered === 'ja-jp' || lowered === 'ja_jp') return 'ja';
  if (lowered === 'zh-tw' || lowered === 'zh_tw' || lowered === 'zh-hant' || lowered === 'zh-hk') return 'zh-TW';
  if (lowered === 'zh-cn' || lowered === 'zh_cn' || lowered === 'zh-hans') return 'zh';

  if (raw === 'zh-TW') return 'zh-TW';
  return lowered;
};

const escapeCsv = (value) => {
  const s = value == null ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
};

const collectDescriptorIds = (node, idsSet) => {
  if (!node) return;

  if (Array.isArray(node)) {
    node.forEach((item) => collectDescriptorIds(item, idsSet));
    return;
  }

  if (typeof node === 'object') {
    if (typeof node.id === 'string' && node.id.trim().length > 0) {
      idsSet.add(node.id.trim());
    }

    Object.values(node).forEach((value) => collectDescriptorIds(value, idsSet));
  }
};

const collectQuestionnaireMessageIds = () => {
  const ids = new Set();

  LANDING_MESSAGE_KEYS.forEach((key) => {
    const descriptor = messages[key];
    if (descriptor && typeof descriptor.id === 'string') {
      ids.add(descriptor.id);
    }
  });

  collectDescriptorIds(slideDataWCS.slides, ids);

  return Array.from(ids);
};

const collectReportMessageIds = () => {
  const ids = new Set();

  Object.entries(messages).forEach(([key, descriptor]) => {
    if (!descriptor || typeof descriptor.id !== 'string' || !descriptor.id.trim()) {
      return;
    }

    if (
      key.startsWith('SCOPE_REPORT_') ||
      key.startsWith('SCOPEREPORT_') ||
      REPORT_EXACT_MESSAGE_KEYS.has(key)
    ) {
      ids.add(descriptor.id.trim());
    }
  });

  return Array.from(ids);
};

const fetchLangFile = async (lang) => {
  const cacheBuster = Date.now();
  const response = await fetch(`/lang/${lang}.json?cb=${cacheBuster}`);
  if (!response.ok) {
    throw new Error(`Failed to load language file for ${lang}`);
  }
  return response.json();
};

export const downloadQuestionnaireTranslationCsv = async ({
  targetLanguage,
  sourceLanguage = 'fr',
  filenamePrefix = 'scope-questionnaire-translations'
}) => {
  const sourceLangFile = normalizeLangForFile(sourceLanguage);
  const targetLangFile = normalizeLangForFile(targetLanguage);

  const [sourceMessages, targetMessages] = await Promise.all([
    fetchLangFile(sourceLangFile),
    fetchLangFile(targetLangFile)
  ]);

  const ids = collectQuestionnaireMessageIds();

  const header = ['message_id', `text_${sourceLangFile}`, `text_${targetLangFile}`]
    .map(escapeCsv)
    .join(',');

  const rows = ids.map((id) => {
    const sourceText = sourceMessages[id] || '';
    const targetText = targetMessages[id] || '';
    return [id, sourceText, targetText].map(escapeCsv).join(',');
  });

  const csvContent = [header, ...rows].join('\n');
  // Prepend UTF-8 BOM so Excel and other tools correctly decode accented and Asian characters
  const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${sourceLangFile}-to-${targetLangFile}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const downloadReportTranslationCsv = async ({
  targetLanguage,
  sourceLanguage = 'fr',
  filenamePrefix = 'scope-report-translations'
}) => {
  const sourceLangFile = normalizeLangForFile(sourceLanguage);
  const targetLangFile = normalizeLangForFile(targetLanguage);

  const [sourceMessages, targetMessages] = await Promise.all([
    fetchLangFile(sourceLangFile),
    fetchLangFile(targetLangFile)
  ]);

  const ids = collectReportMessageIds();

  const header = ['message_id', `text_${sourceLangFile}`, `text_${targetLangFile}`]
    .map(escapeCsv)
    .join(',');

  const rows = ids.map((id) => {
    const sourceText = sourceMessages[id] || '';
    const targetText = targetMessages[id] || '';
    return [id, sourceText, targetText].map(escapeCsv).join(',');
  });

  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${sourceLangFile}-to-${targetLangFile}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
